<template>
  <article class="whats-inside">
    <header
      v-if="introTitle"
      id="header"
      v-observe-visibility="{
        callback: headerVisibilityChanged,
        intersection: {
          threshold: 0.2
        }
      }"
      class="mb-16 text-center px-x-gutter"
    >
      <h1 class="mx-auto heading sm:w-3/4">{{ introTitle }}</h1>
      <div
        v-if="introText"
        class="mx-auto mt-8 text-lg sm:w-4/5 xl:text-xl 2xl:text-2xl"
      >
        <sanity-blocks :blocks="introText" />
      </div>
    </header>

    <section
      v-for="(section, index) of sections"
      :key="section._id"
      class="relative product-category-section"
    >
      <gradient
        :colors="section.backgroundColors"
        force-gradient
        class="absolute bottom-0 left-0 w-full h-full opacity-70"
      />
      <div class="relative flex-wrap md:flex">
        <div :class="`relative flex flex-1 ${index % 2 ? 'md:order-1' : ''}`">
          <sanity-media
            :image="section.image"
            cover
            class="ingredient-section__feature-image"
          />
        </div>
        <div
          class="self-center flex-1 py-8 text-center ingredient-section__feature-text"
        >
          <h3 class="px-6">{{ section.heading }}</h3>
          <div class="px-6 mb-4 text-xl">
            <sanity-blocks
              v-if="section.description"
              :blocks="section.description"
              :container-class="false"
              :containers-are-wide="true"
            />
          </div>
          <cta
            tag="recess-link"
            :sanity-link="{ url: section.ctaHref }"
            fill
            small
            class="link--cta"
          >
            shop {{ section.ctaText }}
          </cta>
        </div>
      </div>
      <ingredient-gallery
        :gallery-options="{
          autoHeight: false,
          breakpoints: {
            768: { centeredSlides: false }
          },
          centeredSlides: true,
          slidesPerView: 'auto',
          watchOverflow: true,
          watchSlidesVisibility: true
        }"
        :ingredients="section.ingredients"
        :minimize-inactive-slides="false"
        :cursor-over="false"
      />
    </section>
    <aside
      v-if="relatedLinks"
      class="flex flex-col items-center my-16 px-x-gutter"
    >
      <h3 v-if="relatedLinks.heading" class="text-center heading">
        {{ relatedLinks.heading }}
      </h3>
      <ul class="mx-auto my-10 related-links">
        <li v-for="link of relatedLinks.links" :key="link._key">
          <a
            class="flex items-baseline w-full px-8 py-4 font-medium sm:text-lg lg:text-xl 2xl:text-2xl"
            :href="link.href"
            target="_blank"
          >
            <span class="font-antidote mr-em">&larr;</span>
            <span>{{ link.title }}</span>
          </a>
        </li>
      </ul>
    </aside>
  </article>
</template>

<script>
import IngredientGallery from '~/components/shop/ingredients/ingredient-gallery.vue'
import page from '~/mixins/page'
import sanityQuery from '~/sanity/queries/whats-inside'
import getDocumentPath from '~/util/sanity/get-document-path'
import {
  getDocumentTextColor,
  getDocumentBackgroundColors
} from '~/util/sanity/document-color'
import getCBDFilter from '~/util/get-cbd-filter'

export default {
  name: 'WhatsInsidePageV2',

  components: { IngredientGallery },

  mixins: [page],

  async asyncData({ error, query, $sanity, $sentry, store }) {
    const previewId = query.preview
    try {
      const pageData = await $sanity.fetch(sanityQuery(previewId))
      if (!pageData) {
        throw new Error('Missing Whats Inside Page')
      }
      const colors = {
        backgroundColors: getDocumentBackgroundColors(pageData),
        textColor: getDocumentTextColor(pageData)
      }
      store.commit('screen/setPageColors', colors)
      return pageData
    } catch (e) {
      $sentry.captureException(e)
      return error({
        statusCode: 404,
        message: e.message
      })
    }
  },

  data: () => {
    return {
      title: null,
      introSplash: null,
      introText: null,
      introTitle: null,
      headerIsVisible: true,
      productCategoryTabs: null,
      productCategorySections: null,
      isCBDSafe: getCBDFilter(process.env.nonCBDSite)
    }
  },

  computed: {
    filteredProductCategorySections() {
      return this.productCategorySections.filter(section =>
        this.isCBDSafe(section.productCategory)
      )
    },
    relatedLinks() {
      if (!this.relatedLinksSection?.links?.length) {
        return undefined
      }
      const relatedLinks = {
        heading: this.relatedLinksSection?.heading || '',
        links: this.relatedLinksSection.links
          .filter(this.isCBDSafe)
          .map(({ page, url, title }) => ({
            title,
            href: page ? getDocumentPath(page) : url
          }))
      }
      return relatedLinks
    },
    sections() {
      const sections = (this.filteredProductCategorySections || []).map(
        section => {
          const backgroundColors =
            section.backgroundColors?.length > 0
              ? section.backgroundColors
              : section.productCategory?.backgroundColors
          return {
            backgroundColors: backgroundColors || [],
            ctaHref: getDocumentPath(section.productCategory ?? {}),
            ctaText: section.productCategory?.title,
            description: section.description,
            heading: section.heading || section.productCategory?.title,
            image: section.image,
            ingredients: section.productCategory?.ingredients ?? []
          }
        }
      )
      return sections
    }
  },
  methods: {
    headerVisibilityChanged(value) {
      this.headerIsVisible = value
    }
  }
}
</script>

<style scoped>
.product-category-section {
  border-bottom: 2px solid theme('colors.titan');
}

.ingredient-section__feature-image {
  min-height: clamp(16rem, 25vw, 24rem);
  width: 100%;
}

.ingredient-section__feature-text h3 {
  font-size: theme('fontSize.2xl');
  font-weight: 500;
  margin-bottom: 1rem;
}

@screen lg {
  .ingredient-section__feature-text h3 {
    font-size: calc(theme('fontSize.2xl') * 1.5);
  }
}

.whats-inside .ingredient-section__feature-text >>> p + p {
  margin-top: 0.5rem;
}

.whats-inside >>> .ingredient-gallery {
  padding-bottom: 0;
}

.whats-inside >>> .swiper-wrapper {
  align-items: stretch;
}

.whats-inside >>> .swiper-slide {
  height: auto;
}

.whats-inside >>> .swiper-pagination {
  margin-bottom: calc(var(--pagination-gutter) / 3);
}

.whats-inside >>> .swiper-pagination-bullet {
  color: theme('colors.titan');
  height: 0.8rem;
  margin: 0 0.25rem;
  width: 0.8rem;
}

.whats-inside >>> .swiper-pagination-bullet-active {
  background-color: theme('colors.lavender');
}

.whats-inside >>> .ingredient-gallery__slide {
  text-align: center;
  width: 60vw;
}

.whats-inside >>> .ingredient__image {
  height: calc(var(--win-height) / 3);
  margin: 15% 0;
}

@screen md {
  .whats-inside >>> .ingredient__image {
    height: calc(var(--win-height) / 4);
  }
}

.whats-inside >>> .ingredient__title {
  margin-bottom: 0.5rem;
}

@screen md {
  .whats-inside >>> .ingredient-gallery__slide {
    border: 1px solid theme('colors.titan');
    border-bottom-width: 0;
    border-top-width: 2px;
    flex: 1 0 25%;
    width: auto;
  }

  .whats-inside >>> .ingredient-gallery__slide::before {
    content: none;
  }
}

@media (hover: hover) {
  .related-links a:hover {
    color: theme('colors.ultramarine');
  }

  .related-links a:hover :not(.font-antidote) {
    text-decoration: underline;
  }
}
</style>
